import { sdk } from '@/services/sdk';
import { idOf, rows } from '@/shared/data';
import { addMonths, termMonths } from '@/shared/pricing';

const { and, eq, isIn, isNotNull } = window.ApperSDK.CoreSDK;

/** A friend's order only earns a referral on a term this long or longer. */
export const MIN_REFERRAL_MONTHS = 6;

/** Free months gifted to the referrer for each referral that counts. */
export const GIFT_MONTHS_PER_REFERRAL = 1;

export const normUsername = (value) => String(value ?? '').trim().toLowerCase();

/** True when the order's term is long enough to earn its referrer a free month. */
export function qualifiesForReferral(order) {
  if (!order?.plan_c || order.plan_c === 'Telly') return false;
  return termMonths(order.plan_c, order.term_c) >= MIN_REFERRAL_MONTHS;
}

/** An active line that has been paid for and has not run out. */
export function isPaidLine(order, now = Date.now()) {
  if (order?.status_c !== 'Active' || !normUsername(order?.line_username_c)) return false;
  return !order.ends_at_c || new Date(order.ends_at_c).getTime() > now;
}

/**
 * The lookup key for a username. Buyers only ever see this hash, never the username
 * itself or the order it belongs to.
 */
export async function usernameKey(value) {
  const name = normUsername(value);
  if (!name) return '';
  if (!window.crypto?.subtle) throw new Error('This browser cannot check usernames. Try another browser.');
  const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(`gc-ref:${name}`));
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Buyer side: is this username an active, paid Ghost Cable line?
 * Returns { ok, message }.
 */
export async function checkReferralUsername(value, ownUsernames = []) {
  const name = normUsername(value);
  if (!name) return { ok: false, message: 'Type your friend’s username.' };
  if (ownUsernames.map(normUsername).includes(name)) {
    return { ok: false, message: 'That’s your own username. Referrals have to come from a friend.' };
  }
  const key = await usernameKey(name);
  const res = await sdk
    .table('referral_handles_c')
    .select(['Name', 'ends_at_c'])
    .where(eq('Name', key))
    .limit(5)
    .fetch();
  if (!res?.success) throw new Error(res?.message || 'Could not check that username.');
  const now = Date.now();
  const live = (res.data ?? []).some((r) => !r.ends_at_c || new Date(r.ends_at_c).getTime() > now);
  return live
    ? { ok: true, message: '' }
    : { ok: false, message: 'That username isn’t an active, paid account.' };
}

/** The buyer's own line usernames, so they can't refer themselves. */
export async function myUsernames() {
  const res = await sdk
    .table('orders_c')
    .select(['line_username_c'])
    .where(isNotNull('line_username_c'))
    .limit(100)
    .fetch();
  return res?.success ? (res.data ?? []).map((o) => o.line_username_c).filter(Boolean) : [];
}

/**
 * Team side: publish or withdraw one order's username so buyers can use it as a referral.
 * Best-effort; never blocks the order action that triggered it.
 */
export async function syncHandle(order) {
  if (!order?.Id) return;
  try {
    const fresh = await sdk
      .table('orders_c')
      .select(['status_c', 'ends_at_c', 'line_username_c'])
      .get(order.Id);
    const line = fresh?.data ?? order;
    const existing = await sdk
      .table('referral_handles_c')
      .select(['Name', 'ends_at_c'])
      .where(eq('order_id_c', order.Id))
      .limit(20)
      .fetch();
    const have = existing?.success ? (existing.data ?? []) : [];
    const key = isPaidLine(line) ? await usernameKey(line.line_username_c) : '';
    const keep = key ? have.find((r) => r.Name === key) : null;
    await Promise.all(have.filter((r) => r !== keep).map((r) => sdk.table('referral_handles_c').remove(r.Id)));
    if (!key) return;
    const ends = line.ends_at_c ?? null;
    if (keep) {
      if ((keep.ends_at_c ?? null) !== ends) await sdk.table('referral_handles_c').update({ Id: keep.Id, ends_at_c: ends });
    } else {
      await sdk.table('referral_handles_c').create({ Name: key, ends_at_c: ends, order_id_c: order.Id });
    }
  } catch {
    // the order is saved either way; the daily sync catches up
  }
}

/**
 * Team side: rebuild the whole username list from the active lines. Runs once a day from
 * the back office and can be run by hand. Returns how many usernames are live.
 */
export async function syncAllHandles() {
  const [linesRes, haveRes] = await Promise.all([
    sdk
      .table('orders_c')
      .select(['status_c', 'ends_at_c', 'line_username_c'])
      .where(and(eq('status_c', 'Active'), isNotNull('line_username_c')))
      .limit(2000)
      .fetch(),
    sdk.table('referral_handles_c').select(['Name', 'ends_at_c', 'order_id_c']).limit(4000).fetch(),
  ]);
  const lines = rows(linesRes).filter((o) => isPaidLine(o));
  const have = rows(haveRes);

  const want = new Map();
  for (const o of lines) {
    want.set(String(o.Id), { Name: await usernameKey(o.line_username_c), ends_at_c: o.ends_at_c ?? null, order_id_c: o.Id });
  }

  const byOrder = new Map();
  const extra = [];
  for (const r of have) {
    const id = String(r.order_id_c ?? '');
    const w = want.get(id);
    if (!w || w.Name !== r.Name || byOrder.has(id)) extra.push(r);
    else byOrder.set(id, r);
  }

  await Promise.all(extra.map((r) => sdk.table('referral_handles_c').remove(r.Id).catch(() => null)));
  const updates = [];
  const creates = [];
  for (const [id, w] of want) {
    const r = byOrder.get(id);
    if (!r) creates.push(w);
    else if ((r.ends_at_c ?? null) !== w.ends_at_c) updates.push({ Id: r.Id, ends_at_c: w.ends_at_c });
  }
  for (let i = 0; i < creates.length; i += 100) await sdk.table('referral_handles_c').create(creates.slice(i, i + 100));
  for (let i = 0; i < updates.length; i += 100) await sdk.table('referral_handles_c').update(updates.slice(i, i + 100));
  return want.size;
}

/** Team side: the live, paid line a buyer typed as their referrer, or null. */
export async function findReferrerLine(username) {
  const name = normUsername(username);
  if (!name) return null;
  const fields = ['Name', 'customer_c', 'email_c', 'status_c', 'ends_at_c', 'line_username_c', 'gift_months_c'];
  const exact = await sdk
    .table('orders_c')
    .select(fields)
    .where(and(eq('status_c', 'Active'), isIn('line_username_c', [...new Set([String(username).trim(), name])])))
    .limit(10)
    .fetch();
  let found = rows(exact).filter((o) => isPaidLine(o));
  if (!found.length) {
    // Usernames are matched without caring about capitals.
    const all = await sdk
      .table('orders_c')
      .select(fields)
      .where(and(eq('status_c', 'Active'), isNotNull('line_username_c')))
      .limit(2000)
      .fetch();
    found = rows(all).filter((o) => isPaidLine(o) && normUsername(o.line_username_c) === name);
  }
  found.sort((a, b) => new Date(b.ends_at_c || 0) - new Date(a.ends_at_c || 0));
  return found[0] ?? null;
}

/** Team side: a referrer's newest live line, where their free month goes when they referred by code. */
export async function latestLineOf(customerId) {
  if (!customerId) return null;
  const res = await sdk
    .table('orders_c')
    .select(['Name', 'customer_c', 'email_c', 'status_c', 'ends_at_c', 'line_username_c', 'gift_months_c'])
    .where(and(eq('customer_c', customerId), eq('status_c', 'Active')))
    .orderByDesc('ends_at_c')
    .limit(10)
    .fetch();
  const list = rows(res);
  return list.find((o) => !o.ends_at_c || new Date(o.ends_at_c).getTime() > Date.now()) ?? list[0] ?? null;
}

/** New end date after gifting free months: from the current end, or from now if it already ran out. */
export function giftedEnd(order, months = GIFT_MONTHS_PER_REFERRAL, now = new Date()) {
  const current = order?.ends_at_c ? new Date(order.ends_at_c) : null;
  if (!current) return null;
  return addMonths(current > now ? current : now, months);
}

export const sameCustomer = (a, b) => !!a && !!b && String(idOf(a)) === String(idOf(b));

/** Team side: withdraw a deleted order's username. */
export async function dropHandle(orderId) {
  if (!orderId) return;
  try {
    const res = await sdk.table('referral_handles_c').select(['Name']).where(eq('order_id_c', orderId)).limit(20).fetch();
    await Promise.all((res?.data ?? []).map((r) => sdk.table('referral_handles_c').remove(r.Id)));
  } catch {
    // the daily sync removes it
  }
}
